import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Euro, TrendingUp, FileText, CreditCard, Wallet, Building2,
  ArrowUpRight, ArrowDownRight, Clock, CheckCircle, AlertCircle,
  Loader2, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => parseFloat(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 });

const FinancesDashboardPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/admin/finances/dashboard`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setData(d); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Layout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div></Layout>
  );

  const kpi = data?.kpi || {};
  const revenueRatio = kpi.total_ca > 0 ? Math.round((kpi.total_facture_ht / kpi.total_ca) * 100) : 0;

  return (
    <Layout>
      <div className="space-y-6" data-testid="finances-dashboard-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Tableau de bord financier</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Vue d'ensemble de l'activité financière de toutes les sessions</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-emerald-500" data-testid="kpi-ca">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wide font-medium">Chiffre d'affaires</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{fmt(kpi.total_ca)} €</p>
                </div>
                <div className="w-11 h-11 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500" data-testid="kpi-facture">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wide font-medium">Total facturé</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{fmt(kpi.total_facture_ht)} €</p>
                  <p className="text-xs text-blue-500 mt-0.5">{revenueRatio}% du CA</p>
                </div>
                <div className="w-11 h-11 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500" data-testid="kpi-paye">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wide font-medium">Total payé</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{fmt(kpi.total_paye)} €</p>
                  <p className="text-xs text-purple-500 mt-0.5">TTC</p>
                </div>
                <div className="w-11 h-11 rounded-xl bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500" data-testid="kpi-solde">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wide font-medium">Solde restant</p>
                  <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">{fmt(kpi.solde_restant)} €</p>
                  <p className="text-xs text-slate-400 mt-0.5">HT à facturer</p>
                </div>
                <div className="w-11 h-11 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                  <Wallet className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Invoice status + Monthly bar chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Invoice status breakdown */}
          <Card data-testid="invoice-status-card">
            <CardContent className="pt-5">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Statut des factures</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-slate-400" />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Brouillons</span>
                  </div>
                  <Badge variant="outline" className="text-sm font-bold" data-testid="count-brouillon">{kpi.brouillon_count}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <ArrowUpRight className="h-4 w-4 text-blue-500" />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Envoyées</span>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700 text-sm font-bold" data-testid="count-envoyees">{kpi.envoyees_count}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                    <span className="text-sm text-slate-700 dark:text-slate-300">Payées</span>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700 text-sm font-bold" data-testid="count-payees">{kpi.payees_count}</Badge>
                </div>
                <div className="border-t border-slate-200 dark:border-slate-700 pt-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-900 dark:text-white">Total factures</span>
                  <span className="text-lg font-bold text-slate-900 dark:text-white" data-testid="count-total">{kpi.factures_count}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Monthly revenue chart */}
          <Card className="lg:col-span-2" data-testid="monthly-chart-card">
            <CardContent className="pt-5">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Facturation mensuelle (HT)</h3>
              {(data?.monthly || []).length > 0 ? (
                <div className="flex items-end gap-1 h-48">
                  {(data?.monthly || []).map((m, i) => {
                    const max = Math.max(...(data?.monthly || []).map(x => x.facture), 1);
                    const h = Math.max((m.facture / max) * 100, 4);
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1" data-testid={`bar-${m.month}`}>
                        <span className="text-[9px] text-slate-400 font-medium">{fmt(m.facture)}</span>
                        <div className="w-full rounded-t-md bg-gradient-to-t from-emerald-500 to-teal-400 transition-all" style={{ height: `${h}%` }} />
                        <span className="text-[9px] text-slate-400">{m.month.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                  <Euro className="h-8 w-8 mr-2 opacity-30" />
                  Aucune donnée de facturation disponible
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Per-session financial summary + Recent invoices */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sessions */}
          <Card data-testid="sessions-finance-card">
            <CardContent className="pt-5">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Facturation par session</h3>
              {(data?.sessions || []).length > 0 ? (
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {(data?.sessions || []).map((s, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" data-testid={`session-finance-${i}`} onClick={() => navigate(`/sessions/${s.session_id}/edit`)}>
                      <div className="flex items-center gap-3 min-w-0">
                        <Building2 className="h-4 w-4 text-emerald-500 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{s.title}</p>
                          <p className="text-[10px] text-slate-400">{s.factures_count} facture{s.factures_count !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-sm font-bold text-slate-900 dark:text-white">{fmt(s.total_ht)} €</span>
                        <ChevronRight className="h-4 w-4 text-slate-300" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-8">Aucune session avec des factures</p>
              )}
            </CardContent>
          </Card>

          {/* Recent invoices */}
          <Card data-testid="recent-invoices-card">
            <CardContent className="pt-5">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Dernières factures</h3>
              {(data?.recent_factures || []).length > 0 ? (
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                  {(data?.recent_factures || []).map((f, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg" data-testid={`recent-facture-${i}`}>
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-blue-500" />
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-white">{f.prefix}-{f.number}</p>
                          <p className="text-[10px] text-slate-400">{f.client_name} · {f.date}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-[10px] ${f.status === 'payee' ? 'border-emerald-300 text-emerald-600' : f.status === 'envoyee' ? 'border-blue-300 text-blue-600' : 'border-slate-300 text-slate-500'}`}>{f.status}</Badge>
                        <span className="text-sm font-bold text-slate-900 dark:text-white">{fmt(f.total_ttc)} €</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-8">Aucune facture récente</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default FinancesDashboardPage;
